import {FC, useState} from 'react'
import {createPortal} from 'react-dom'
import {Dropdown, FormControl} from 'react-bootstrap'
import {getISO2ByISO3} from '../../../../libs/misc'

export type RegionOption = {code: string; name: string}

// CGE region codes (lowercase, e.g. "eu27") and TINA tariff-entity codes
// (uppercase ISO3, e.g. "EUN") both funnel through here to reuse
// getISO2ByISO3's existing EUN -> "EU" special-case (same helper
// TradePartnerDropdown/EconDropdown use for their flags).
const REGION_TO_ISO3: Record<string, string> = {
  bgd: 'BGD',
  can: 'CAN',
  chn: 'CHN',
  eu27: 'EUN',
  gbr: 'GBR',
  ind: 'IND',
  jpn: 'JPN',
  usa: 'USA',
}

// row/seasia (and anything else with no real-country flag) fall back to a
// globe icon, same convention TradePartnerDropdown uses for "WLD". An ASEAN
// flag for seasia would be nicer but isn't in the installed flag-icons set
// (ISO-3166 based, only bonus non-ISO entries are "eu"/"un"). Swap this
// in if a real ASEAN SVG asset gets added later.
export const regionFlagIcon = (code: string) => {
  const key = code.toLowerCase()
  const iso3 = REGION_TO_ISO3[key] ?? (code.length === 3 ? code.toUpperCase() : undefined)
  const iso2 = iso3 ? getISO2ByISO3(iso3) : undefined
  if (iso2 && iso2 !== 'XX') {
    return <span className={`fi fi-${iso2.toLowerCase()} me-2`}></span>
  }
  return <i className='fa-solid fa-globe me-2'></i>
}

export type RegionSection = {label: string; options: RegionOption[]}

type RegionDropdownProps = {
  // Either a flat list (original behavior, e.g. the direct-shocks table's
  // metadata.regions) or a `sections` list to render grouped with headers
  // (e.g. the commodity form's Core economies / South East Asia / Rest of
  // World reporter-partner pickers). Exactly one of the two is expected.
  options?: RegionOption[]
  sections?: RegionSection[]
  value: string
  onChange: (code: string) => void
  placeholder?: string
}

// Same flag-dropdown chrome as EconDropdown/TradePartnerDropdown (FTA
// impact), scoped to a small fixed option list instead of a live-fetched,
// searchable economy list. No search bar needed for ~10 options.
const RegionDropdown: FC<RegionDropdownProps> = ({
  options,
  sections,
  value,
  onChange,
  placeholder = 'Select...',
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const resolvedSections: RegionSection[] = sections ?? [{label: '', options: options ?? []}]
  const selected = resolvedSections.flatMap((s) => s.options).find((o) => o.code === value)

  const query = searchTerm.trim().toLowerCase()
  const filteredSections = query
    ? resolvedSections
        .map((s) => ({
          ...s,
          options: s.options.filter(
            (o) => o.name.toLowerCase().includes(query) || o.code.toLowerCase().includes(query)
          ),
        }))
        .filter((s) => s.options.length > 0)
    : resolvedSections

  return (
    <Dropdown onToggle={() => setSearchTerm('')}>
      <Dropdown.Toggle
        variant='label-dropdown'
        id=''
        className='btn btn-sm btn-bold dropdown-toggle fw-normal text-start w-100'
        style={{
          display: 'flex',
          height: '2.6rem',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {selected ? (
          <span style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'clip'}}>
            {regionFlagIcon(selected.code)}
            {selected.name}
          </span>
        ) : (
          placeholder
        )}
      </Dropdown.Toggle>
      {/* Portaled to <body>: CGEShockForm renders this inside a
          .table-responsive (overflow-x: auto), which clips the menu at the
          table's edge if it stays in place. Popper positions it under the
          toggle via refs shared through DropdownContext, so the portal
          doesn't need to live near the toggle in the DOM. Fixed width (not
          w-100) since w-100 would otherwise stretch to the body's width. */}
      {createPortal(
        <Dropdown.Menu
          className='dropdown-econ-menu'
          style={{maxHeight: '20rem', overflowY: 'auto'}}
        >
          <FormControl
            type='text'
            size='sm'
            autoFocus
            placeholder='Search...'
            className='dropdown-econ-search-bar'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
          {filteredSections.length === 0 && (
            <div className='text-muted small px-3 py-2'>No matches</div>
          )}
          {filteredSections.map((section, i) => (
            <div key={section.label || i}>
              {section.label && <Dropdown.Header>{section.label}</Dropdown.Header>}
              {section.options.map((o) => (
                <Dropdown.Item
                  key={o.code}
                  role='menuitem'
                  onClick={() => onChange(o.code)}
                  title={o.name}
                >
                  {regionFlagIcon(o.code)}
                  {o.name}
                </Dropdown.Item>
              ))}
            </div>
          ))}
        </Dropdown.Menu>,
        document.body
      )}
    </Dropdown>
  )
}

export default RegionDropdown
